"use client";

import { createContext, useContext, useRef } from "react";
import { useRadio, useRadioGroup } from "@react-aria/radio";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { useRadioGroupState } from "@react-stately/radio";
import { csx } from "../../utils";

import "./ButtonGroup.css";

export interface ButtonGroupProps {
  /**
   * The value of the currently selected option in the ButtonGroup. Providing
   * this prop causes the component to become controlled.
   */
  value?: string;

  /**
   * The initially selected value of the ButtonGroup.
   */
  defaultValue?: string;

  /**
   * Called when the ButtonGroup's selected value changes.
   */
  onChange?: (value: string) => void;

  /**
   * The ButtonGroup.Option elements to be rendered as the selectable values.
   */
  children?: React.ReactNode;
}

let ButtonGroupContext = createContext(null);

/**
 * A radio button group component that allows selection of a single option from multiple choices.
 *
 * API:
 *  - {@link ButtonGroupProps}
 */
export function ButtonGroup(props: ButtonGroupProps) {
  let sanitizedProps = {
    ...props,
    "data-testid": undefined,
  };

  let state = useRadioGroupState(sanitizedProps);
  let { radioGroupProps } = useRadioGroup(sanitizedProps, state);

  return (
    <div
      {...radioGroupProps}
      className="proton-ButtonGroup"
      data-value={state.selectedValue}
      data-testid={props["data-testid"] || undefined}
    >
      <ButtonGroupContext.Provider value={state}>
        {props.children}
      </ButtonGroupContext.Provider>
    </div>
  );
}

ButtonGroup.displayName = "ProtonUIButtonGroup";

export interface ButtonGroupOptionProps {
  /**
   * The value of this option. When this option is selected, this value will
   * become the ButtonGroup's new `selectedValue`.
   */
  value: string;

  /**
   * The text or component to be rendered as this option's content.
   */
  children: React.ReactNode;
}

ButtonGroup.Option = function ButtonGroupOption(props: ButtonGroupOptionProps) {
  let sanitizedProps = {
    ...props,
    "data-testid": undefined,
  };

  let state = useContext(ButtonGroupContext);
  let ref = useRef(null);
  let { inputProps, isSelected } = useRadio(sanitizedProps, state, ref);

  return (
    <label
      className={csx(
        "proton-ButtonGroup__option",
        isSelected && "proton-ButtonGroup__option--selected"
      )}
      data-selected={isSelected || undefined}
      data-testid={props["data-testid"] || undefined}
    >
      <VisuallyHidden>
        <input {...inputProps} ref={ref} />
      </VisuallyHidden>
      {props.children}
    </label>
  );
};
